import { useEffect, useRef, useState } from 'react'
import { forRecording, releaseRecording } from '../lib/audio-session.ts'

/**
 * ЗАПИСЬ ВИДЕОСООБЩЕНИЯ
 *
 * Круглое и короткое — минута потолок. Такой ролик снимают, чтобы сказать
 * пару слов, а не рассказать историю: длинный никто не досмотрит,
 * а весить он будет как десять фотографий.
 *
 * Снимаем квадрат и обрезаем кругом уже при показе: обрезать само видео
 * потребовало бы перекодирования, а выглядит одинаково.
 */

const MAX_SECONDS = 60
const SIDE = 480

type Props = {
  onSend: (file: File, durationSec: number) => void
  onCancel: () => void
}

const isSafari = (): boolean =>
  /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|CriOS|Edg|FxiOS|Android/.test(navigator.userAgent)

function pickMimeType(): string {
  // На iPhone — mp4: webm он пишет без длительности и сам же не умеет его перематывать
  const candidates = isSafari()
    ? ['video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm']
    : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

export function VideoRecorder({ onSend, onCancel }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const stream = useRef<MediaStream | null>(null)
  const startedAt = useRef(0)
  const finishing = useRef(false)

  const [seconds, setSeconds] = useState(0)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const teardown = () => {
    stream.current?.getTracks().forEach((track) => track.stop())
    releaseRecording()
    stream.current = null
  }

  useEffect(() => {
    let alive = true

    const start = async () => {
      try {
        forRecording()
        const media = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: SIDE, height: SIDE, aspectRatio: 1 },
          audio: { echoCancellation: true, noiseSuppression: true },
        })
        if (!alive) {
          media.getTracks().forEach((t) => t.stop())
          return
        }

        stream.current = media
        if (videoRef.current) {
          videoRef.current.srcObject = media
          // Своё видео всегда без звука: иначе микрофон ловит динамик
          // и получается вой
          videoRef.current.muted = true
          await videoRef.current.play().catch(() => {})
        }

        const mimeType = pickMimeType()
        const rec = new MediaRecorder(media, mimeType ? { mimeType, videoBitsPerSecond: 900_000 } : undefined)
        recorder.current = rec
        chunks.current = []

        rec.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.current.push(event.data)
        }

        rec.onstop = () => {
          teardown()
          if (!finishing.current) return

          const blob = new Blob(chunks.current, { type: rec.mimeType || 'video/webm' })
          const extension = (rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm'
          const duration = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000))

          onSend(new File([blob], `кружок.${extension}`, { type: blob.type }), duration)
        }

        startedAt.current = Date.now()
        rec.start()
        setReady(true)
      } catch {
        setError('Нет доступа к камере или микрофону. Разреши его в настройках браузера.')
      }
    }

    void start()

    return () => {
      alive = false
      finishing.current = false
      if (recorder.current?.state === 'recording') recorder.current.stop()
      teardown()
    }
  }, [])

  useEffect(() => {
    if (!ready || error) return
    const timer = window.setInterval(() => {
      setSeconds((value) => {
        if (value + 1 >= MAX_SECONDS) {
          finish()
          return MAX_SECONDS
        }
        return value + 1
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [ready, error])

  const finish = () => {
    finishing.current = true
    if (recorder.current?.state === 'recording') recorder.current.stop()
    else onCancel()
  }

  const drop = () => {
    finishing.current = false
    if (recorder.current?.state === 'recording') recorder.current.stop()
    teardown()
    onCancel()
  }

  return (
    <div className="video-rec-overlay" onClick={drop}>
      <div className="video-rec" onClick={(e) => e.stopPropagation()}>
        {error ? (
          <>
            <p className="sheet-text">{error}</p>
            <button className="btn-quiet" onClick={onCancel}>
              Закрыть
            </button>
          </>
        ) : (
          <>
            <div className="video-rec-circle">
              {/* Отражаем по горизонтали: человек привык видеть себя
                  как в зеркале, иначе кажется, что что-то не так */}
              <video ref={videoRef} playsInline muted />
              <span
                className="video-rec-ring"
                style={{ ['--progress' as string]: `${(seconds / MAX_SECONDS) * 100}%` }}
              />
            </div>

            <div className="video-rec-time">
              <span className="recorder-dot" aria-hidden="true" />
              {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
              <span className="video-rec-limit">из 1:00</span>
            </div>

            <div className="video-rec-actions">
              <button className="btn-quiet" onClick={drop}>
                Отменить
              </button>
              <button className="btn-primary" onClick={finish} disabled={!ready}>
                Отправить
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
